import type { FormDefinition, FormField, FormSubmitInput, FormToFill, FormsPacket } from "@vaulted/shared";
import { formatPhone } from "@vaulted/shared";
import { createContext, useContext, useEffect, useState } from "react";
import { errorMessage } from "../api";
import { type AddressLookup, AddressField } from "./AddressField";
import { DrawPad } from "./SignatureInput";
import { Alert, Button, Checkbox, Field, SelectField, TextAreaField, cx } from "./ui";

/** Where the street address looks up suggestions: the forms link or iPad; none (staff) uses the staff lookup. */
const AddressLookupContext = createContext<AddressLookup | undefined>(undefined);

type Answers = Record<string, unknown>;
type Details = NonNullable<FormSubmitInput["details"]>;
type Guardian = NonNullable<FormSubmitInput["guardian"]>;

/** One form's sections, fields, and (when filling) inputs. Read-only shows a signed copy. */
export function FormBody({
  def,
  minor,
  answers,
  setAnswers,
  details,
  setDetails,
  guardian,
  setGuardian,
  readOnly,
}: {
  def: FormDefinition;
  minor: boolean;
  answers: Answers;
  setAnswers?: (a: Answers) => void;
  details: Details | null;
  setDetails?: (d: Details) => void;
  guardian: Guardian | null;
  setGuardian?: (g: Guardian) => void;
  readOnly?: boolean;
}) {
  const set = (key: string, v: unknown) => setAnswers?.({ ...answers, [key]: v });
  const field = (f: FormField, i: number) => {
    switch (f.kind) {
      case "text":
        return (
          <p key={i} className="text-[15px] leading-relaxed text-ink-soft">
            {f.text}
          </p>
        );
      case "bullets":
        return (
          <div key={i}>
            {f.title && <h4 className="font-semibold">{f.title}</h4>}
            <ul className="mt-1 list-disc pl-5 text-[15px] leading-relaxed text-ink-soft">
              {f.items.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        );
      case "client_details":
        return details ? <DetailsBlock key={i} d={details} onChange={setDetails} readOnly={readOnly} /> : null;
      case "guardian_details":
        return (
          <div key={i} className="grid gap-3 sm:grid-cols-3">
            <Field label="Parent or guardian's full name" required disabled={readOnly} value={guardian?.name ?? ""} onChange={(e) => setGuardian?.({ ...(guardian ?? { relationship: "", phone: "" }), name: e.target.value })} />
            <Field label="Relationship to minor" required disabled={readOnly} value={guardian?.relationship ?? ""} onChange={(e) => setGuardian?.({ ...(guardian ?? { name: "", phone: "" }), relationship: e.target.value })} />
            <Field label="Phone" type="tel" disabled={readOnly} value={guardian?.phone ?? ""} onChange={(e) => setGuardian?.({ ...(guardian ?? { name: "", relationship: "" }), phone: e.target.value })} />
          </div>
        );
      case "input":
        return f.multiline ? (
          <TextAreaField key={i} label={f.label} required={f.required} disabled={readOnly} value={String(answers[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} />
        ) : (
          <Field key={i} label={f.label} required={f.required} disabled={readOnly} value={String(answers[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} />
        );
      case "checklist":
      case "choice": {
        // Asked only of clients who aren't male; answered for male clients.
        if (f.maleAnswer && details?.sexAtBirth === "male") return null;
        const picked = f.kind === "choice" ? (typeof answers[f.key] === "string" && answers[f.key] ? [answers[f.key] as string] : []) : Array.isArray(answers[f.key]) ? (answers[f.key] as string[]) : [];
        const showDetails = f.details && picked.some((p) => f.details!.forOptions.includes(p));
        const detailsBox = showDetails && (
          <TextAreaField label={f.details!.label} required rows={2} disabled={readOnly} value={String(answers[f.details!.key] ?? "")} onChange={(e) => set(f.details!.key, e.target.value)} />
        );
        if (readOnly)
          return (
            <div key={i}>
              <p className="text-sm font-semibold text-ink-soft">{f.label}</p>
              <p className="font-semibold">{picked.length ? picked.join(", ") : "Not answered"}</p>
              {showDetails && <p className="whitespace-pre-wrap">{String(answers[f.details!.key] ?? "")}</p>}
            </div>
          );
        const toggle = (o: string, on: boolean) => {
          if (f.kind === "choice") return set(f.key, o);
          // "None of the above" clears the others, and any other choice clears it.
          if (on && f.none && o === f.none) return set(f.key, [o]);
          const rest = picked.filter((x) => x !== o && x !== f.none);
          set(f.key, on ? [...rest, o] : rest);
        };
        return (
          <fieldset key={i} className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">
              {f.label}
              {f.required && <span className="text-danger"> *</span>}
            </legend>
            <div className={cx("grid", f.options.length > 4 && "sm:grid-cols-2")}>
              {f.options.map((o) =>
                f.kind === "choice" ? (
                  <label key={o} className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] px-2 py-2 hover:bg-mist">
                    <input type="radio" name={f.key} className="mt-1 size-4 accent-[var(--color-brand)]" checked={picked.includes(o)} onChange={() => toggle(o, true)} />
                    <span>{o}</span>
                  </label>
                ) : (
                  <Checkbox key={o} label={o} checked={picked.includes(o)} onChange={(on) => toggle(o, on)} />
                ),
              )}
            </div>
            {detailsBox}
          </fieldset>
        );
      }
      case "agree":
        if (readOnly) return <p key={i} className="text-sm">{answers[f.key] === true ? "☑" : "☐"} {f.label}</p>;
        return (
          <div key={i} className="rounded-[var(--radius-control)] border border-line">
            <Checkbox label={f.label} disabled={readOnly} checked={answers[f.key] === true} onChange={(on) => set(f.key, on)} />
          </div>
        );
    }
  };
  return (
    <div className="flex flex-col gap-6">
      {def.sections
        .filter((s) => !s.only || (s.only === "minor") === minor)
        .map((s, i) => (
          <section key={i} className="flex flex-col gap-3">
            <h3 className="font-display text-lg">{s.title}</h3>
            {s.fields.map(field)}
          </section>
        ))}
    </div>
  );
}

const phone = (p: string | null) => (p ? formatPhone(p) : p);

function DetailsBlock({ d: raw, onChange, readOnly }: { d: Details; onChange?: (d: Details) => void; readOnly?: boolean }) {
  const d = readOnly ? { ...raw, mobile: phone(raw.mobile), emergencyPhone: phone(raw.emergencyPhone) } : raw;
  const set = (patch: Partial<Details>) => onChange?.({ ...d, ...patch });
  const addr = d.address ?? { line1: "", line2: "", city: "", state: "", postalCode: "" };
  const setAddr = (patch: Partial<typeof addr>) => set({ address: { ...addr, ...patch } });
  const lookup = useContext(AddressLookupContext);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="First name" disabled value={d.firstName} />
      <Field label="Last name" disabled value={d.lastName} />
      <Field label="Date of birth" disabled value={d.dob} hint={readOnly ? undefined : "To change your name or date of birth, tell us at your visit."} />
      <SelectField label="Sex assigned at birth" disabled={readOnly} value={d.sexAtBirth ?? ""} onChange={(e) => set({ sexAtBirth: (e.target.value || null) as Details["sexAtBirth"] })}>
        <option value="">Choose…</option>
        <option value="female">Female</option>
        <option value="male">Male</option>
        <option value="intersex">Intersex</option>
        <option value="unknown">Prefer not to say</option>
      </SelectField>
      <Field label="Mobile phone" type="tel" disabled={readOnly} value={d.mobile ?? ""} onChange={(e) => set({ mobile: e.target.value })} />
      <Field label="Email" type="email" disabled={readOnly} value={d.email ?? ""} onChange={(e) => set({ email: e.target.value })} />
      {readOnly ? (
        <Field label="Street address" className="sm:col-span-2" disabled value={addr.line1} />
      ) : (
        <AddressField
          label="Street address"
          className="sm:col-span-2"
          placeholder="Start typing your street address"
          lookup={lookup}
          value={addr.line1}
          onChange={(line1) => setAddr({ line1 })}
          onResolved={(a) => setAddr({ line1: a.line1, line2: a.line2 || addr.line2, city: a.city, state: a.state, postalCode: a.postalCode })}
        />
      )}
      <Field label="City" disabled={readOnly} value={addr.city} onChange={(e) => setAddr({ city: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="State" disabled={readOnly} value={addr.state} onChange={(e) => setAddr({ state: e.target.value })} />
        <Field label="ZIP" disabled={readOnly} value={addr.postalCode} onChange={(e) => setAddr({ postalCode: e.target.value })} />
      </div>
      <Field label="Emergency contact name" disabled={readOnly} value={d.emergencyName ?? ""} onChange={(e) => set({ emergencyName: e.target.value })} />
      <Field label="Emergency contact phone" type="tel" disabled={readOnly} value={d.emergencyPhone ?? ""} onChange={(e) => set({ emergencyPhone: e.target.value })} />
    </div>
  );
}

/** The client's health screening after the forms: start it (Qualiphy's page), or have the link sent for later. */
export interface ScreeningActions {
  start: () => Promise<string | null>;
  /** Text and email the link; returns the last 4 digits of the phone it went to, if known. */
  later: () => Promise<string | null | void>;
  /** A clinic iPad that doesn't do telehealth: the link can only be sent to their phone. */
  sendOnly?: boolean;
  /** After the link is sent (the iPad moves on by itself). */
  onSent?: () => void;
}

/** Fill in and sign each form the visit needs, one at a time; then the health screening, if there is one. */
export function FormsFill(props: {
  packet: FormsPacket;
  onSubmit: (v: FormSubmitInput) => Promise<FormsPacket>;
  doneNote: string;
  screening?: ScreeningActions;
  addressLookup?: AddressLookup;
  /** Opened in its own tab (the forms link): "Done" closes it, back to the booking. */
  closable?: boolean;
}) {
  return (
    <AddressLookupContext.Provider value={props.addressLookup}>
      <FormsSteps {...props} />
    </AddressLookupContext.Provider>
  );
}

function FormsSteps({
  packet,
  onSubmit,
  doneNote,
  screening,
  closable,
}: {
  packet: FormsPacket;
  onSubmit: (v: FormSubmitInput) => Promise<FormsPacket>;
  doneNote: string;
  screening?: ScreeningActions;
  closable?: boolean;
}) {
  const [current, setCurrent] = useState(packet);
  // Browsers only let a page close a tab it was opened into; if it stays open, say so.
  const [stillOpen, setStillOpen] = useState(false);
  const close = () => {
    window.close();
    setTimeout(() => setStillOpen(true), 300);
  };
  const form = current.needed[0];
  const total = current.needed.length + current.done.length;
  if (!form && screening && current.screening && current.screening.state !== "after_forms") {
    return <ScreeningStep minor={current.client.minor} started={current.screening.state === "started"} actions={screening} mobile={current.client.details.mobile} />;
  }
  if (!form) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
        <h2 className="font-display text-2xl">You're all set</h2>
        <p className="max-w-md text-ink-soft">{doneNote}</p>
        {current.done.length > 0 && <p className="text-sm text-muted">Signed: {current.done.map((d) => d.name).join(", ")}</p>}
        {closable &&
          (stillOpen ? (
            <p className="text-sm font-semibold text-ink-soft">You can close this tab now.</p>
          ) : (
            <Button variant="primary" className="mt-2 px-8" onClick={close}>
              Done
            </Button>
          ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">
        Form {current.done.length + 1} of {total}
        {screening && current.screening ? ", then a quick health screening" : ""}
      </p>
      <OneForm key={form.versionId} form={form} packet={current} onSubmit={async (v) => setCurrent(await onSubmit(v))} />
    </div>
  );
}

function OneForm({ form, packet, onSubmit }: { form: FormToFill; packet: FormsPacket; onSubmit: (v: FormSubmitInput) => Promise<void> }) {
  const def = form.definition;
  const hasDetails = def.sections.some((s) => s.fields.some((f) => f.kind === "client_details"));
  const needsGuardian = packet.client.minor && def.sections.some((s) => s.only === "minor" && s.fields.some((f) => f.kind === "guardian_details"));
  // Returning clients start from their last answers (consents are ticked again each time).
  const [answers, setAnswers] = useState<Answers>(() => {
    const agreeKeys = new Set(def.sections.flatMap((s) => s.fields.flatMap((f) => (f.kind === "agree" ? [f.key] : []))));
    return Object.fromEntries(Object.entries(form.previous ?? {}).filter(([k]) => !agreeKeys.has(k)));
  });
  const [details, setDetails] = useState<Details>(() => ({ ...packet.client.details, mobile: phone(packet.client.details.mobile), emergencyPhone: phone(packet.client.details.emergencyPhone) }));
  const [guardian, setGuardian] = useState<Guardian | null>(packet.client.guardian);
  const [signedName, setSignedName] = useState("");
  // Drawn by default; typing the name is the alternative.
  const [signMode, setSignMode] = useState<"draw" | "type">("draw");
  const [drawn, setDrawn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signer = packet.client.minor ? "Parent or guardian's full legal name" : "Your full legal name";
  // The name is typed only when it's the signature, or an adult signs for a child.
  const typeName = signMode === "type" || packet.client.minor;

  const submit = async () => {
    if (signMode === "draw" && !drawn) return setError("Please sign in the box (or choose to type your signature).");
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        templateId: form.templateId,
        versionId: form.versionId,
        answers: answers as FormSubmitInput["answers"],
        details: hasDetails ? { ...details, address: details.address?.line1 ? details.address : null } : null,
        guardian: needsGuardian ? guardian : null,
        signedName: typeName ? signedName : "",
        signature: signMode === "draw" ? drawn : null,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <header>
        <h2 className="font-display text-2xl">{def.title}</h2>
        {def.subtitle && <p className="text-ink-soft">{def.subtitle}</p>}
      </header>
      <FormBody def={def} minor={packet.client.minor} answers={answers} setAnswers={setAnswers} details={details} setDetails={setDetails} guardian={guardian} setGuardian={setGuardian} />
      <section className={cx("flex flex-col gap-3 rounded-[var(--radius-panel)] border border-line bg-mist/40 p-4")}>
        <h3 className="font-semibold">Sign</h3>
        {typeName ? (
          <Field label={signer} required autoComplete="name" value={signedName} onChange={(e) => setSignedName(e.target.value)} />
        ) : (
          <p className="text-sm">
            Signing as{" "}
            <b>
              {packet.client.firstName} {packet.client.lastName}
            </b>
          </p>
        )}
        {signMode === "draw" ? (
          <>
            <p className="text-sm text-ink-soft">Sign in the box with your finger (or mouse). It's your electronic signature, saved with today's date and this exact form.</p>
            <DrawPad onChange={setDrawn} />
          </>
        ) : (
          <p className="text-sm text-ink-soft">Typing your name above is your electronic signature. It's saved with today's date and this exact form.</p>
        )}
        <button
          type="button"
          className="self-start text-sm font-semibold text-brand underline"
          onClick={() => (setSignMode(signMode === "draw" ? "type" : "draw"), setDrawn(null), setError(null))}
        >
          {signMode === "draw" ? "Type my signature instead" : "Draw my signature instead"}
        </button>
        <p className="text-sm text-muted">Date: {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}</p>
      </section>
      {error && <Alert>{error}</Alert>}
      <Button variant="primary" type="submit" busy={busy} className="self-start px-6 py-3 text-base">
        Sign and continue
      </Button>
    </form>
  );
}

function ScreeningStep({ minor, started, actions, mobile }: { minor: boolean; started: boolean; actions: ScreeningActions; mobile: string | null }) {
  const [busy, setBusy] = useState<"start" | "later" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentLater, setSentLater] = useState(false);
  const [ending, setEnding] = useState<string | null>(null);
  // Sent: the iPad moves on after a moment.
  useEffect(() => {
    if (!sentLater || !actions.onSent) return;
    const t = setTimeout(actions.onSent, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentLater]);
  const go = async (which: "start" | "later") => {
    setBusy(which);
    setError(null);
    try {
      if (which === "start") {
        const url = await actions.start();
        if (url) window.location.href = url;
      } else {
        setEnding((await actions.later()) || null);
        setSentLater(true);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  if (sentLater) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
        <h2 className="font-display text-2xl">Your forms are done</h2>
        <p className="max-w-md text-ink-soft">
          We just {ending ? `texted your phone ending ${ending} (and emailed you)` : "texted and emailed you"} the link to your health screening. Please finish it before your visit.
        </p>
      </div>
    );
  }
  // This iPad doesn't do the video call: the link goes to their phone.
  if (actions.sendOnly) {
    const to = mobile ? `my phone ending ${mobile.replace(/\D/g, "").slice(-4)}` : minor ? "my parent's phone" : "my phone";
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
        <h2 className="font-display text-2xl">Forms done. One more step</h2>
        <p className="max-w-md text-ink-soft">
          Before your IV or injection, a licensed provider does a quick good faith exam: a short video call, about 5 to 10 minutes. We'll send the link to your phone so you can do it there.
        </p>
        {minor && <p className="max-w-md text-sm font-semibold text-wait">A parent or guardian must be with them for the call.</p>}
        {error && <Alert>{error}</Alert>}
        <Button variant="primary" className="w-full max-w-sm py-3" busy={busy === "later"} disabled={!!busy} onClick={() => void go("later")}>
          Send my telehealth screening to {to}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
      <h2 className="font-display text-2xl">{started ? "Finish your health screening" : "Forms done. One more step"}</h2>
      <p className="max-w-md text-ink-soft">
        Before your IV or injection, a licensed provider does a quick good faith exam: a short video call, about 5 to 10 minutes. Your answers from the intake are already filled in. You'll need your camera and a quiet spot.
      </p>
      {minor && <p className="max-w-md text-sm font-semibold text-wait">A parent or guardian must be with them for the call.</p>}
      {error && <Alert>{error}</Alert>}
      <div className="flex w-full max-w-sm flex-col gap-2">
        <Button variant="primary" busy={busy === "start"} disabled={!!busy} onClick={() => void go("start")}>
          {started ? "Continue my health screening" : "Start my health screening"}
        </Button>
        {!started && (
          <Button variant="quiet" busy={busy === "later"} disabled={!!busy} onClick={() => void go("later")}>
            I'll do it later (text me the link)
          </Button>
        )}
      </div>
    </div>
  );
}
